<template>
    <div>
        <div v-if="isHasConcernSubstancesSwitcherVisible || !showComponentsSubstances" class="mb-1">
            <ChangeLogModal
                name="compositionChangeLog"
                ref="changelog"
                :relations-label="$t('Component(Composition):changelog-substances-label')"
                :primary-key="product.id"
                business-object="ProductSubstances"
                namespace="subscriber"
            />
            <div class="text-right">
                <Button class="btn-sm" @click="$refs.changelog.show()">{{ $t('Component(Button):changelog') }}</Button>
            </div>
        </div>
        <b-alert
            variant="dark"
            show
            v-if="isHasConcernSubstancesSwitcherVisible"
        >
            <h4 class="alert-heading">
                {{ $t('Component(Composition):includes-ce-question-headline') }}
            </h4>
            <div class="alert-body pb-2 demo-inline-spacing text-center">
                <b-form-radio
                    name="some-radios"
                    :value="true"
                    v-model="product.hasConcernSubstances"
                    @change="productHasConcernElements(true)"
                >
                    {{ $t('Component(Choice):yes') }}
                </b-form-radio>
                <b-form-radio
                    name="some-radios"
                    :value="false"
                    v-model="product.hasConcernSubstances"
                    @change="productHasConcernElements(false)"
                >
                    {{ $t('Component(Choice):no') }}
                </b-form-radio>
            </div>
        </b-alert>
        <div v-if="!showComponentsSubstances && (hasConcernElements || concernSubstances.length > 0)">
            <div class="pb-2">
                <b-form-group :label="$t('Component(Composition):search-label')">
                    <vue-autosuggest
                        :suggestions="suggestions"
                        :input-props="{placeholder: $t('Component(Composition):search-placeholder'), class: 'form-control w-100',}"
                        :get-suggestion-value="getSuggestionValue"
                        @selected="include"
                        @input="findSubstance"
                    >
                        <div slot-scope="{suggestion}">
                            <div>
                                <div class="pb-1">
                                    <strong>{{suggestion.item.title}}</strong>
                                </div>
                                <div>
                                    <b-badge variant="success" class="mr-1" v-if="isSelected(suggestion)">
                                        selected
                                    </b-badge>
                                    <b-badge class="mr-1">CAS: {{ suggestion.item.cas }}</b-badge>
                                    <b-badge>EC: {{ suggestion.item.ec }}</b-badge>
                                </div>
                            </div>
                        </div>
                    </vue-autosuggest>
                </b-form-group>
            </div>

            <div v-for="substance in substances">
                <Substance
                    :substance="substance"
                    :read-only="false"
                    :compact-view="compactView"
                    :substance-lists="substanceLists"
                    @exclude="exclude"
                    @change="substanceConfigurationChanged"
                />
            </div>
        </div>
        <div v-else-if="showComponentsSubstances">
            <ComponentList
                :compact-view="compactView"
                :substance-lists="substanceLists"
                :components="components"
            />
        </div>
    </div>
</template>

<script>
import { BAlert, BFormGroup, BFormRadio, BCard, BCardTitle, BRow, BCol, BBadge, BButton, BLink } from 'bootstrap-vue'
import { VueAutosuggest } from 'vue-autosuggest'
import {ProductApi, SubstanceApi} from "@core/api";
import {ProductProvider} from "@core/domain/product";
import ComponentList from "@/views/product/components/product-card/composition/ComponentList.vue";
import {Substance} from "@/views/product/components/product-card/composition";
import {getAllSubstanceLists} from "@core/domain/substance";
import ChangeLogModal from "@core/components/common/changelog/ChangeLogModal.vue";
import Button from "@core/components/common/Button.vue";
import SearchOrAddSubstance from "@/views/product/components/product-card/SearchOrAddSubstance.vue";
import SubstanceListReferenceGraphMixin from "@/views/product/components/product-card/SubstanceListReferenceGraphMixin";

export default {
    mixins: [SubstanceListReferenceGraphMixin],
    components: {
        SearchOrAddSubstance,
        Button, ChangeLogModal,
        BFormGroup, BLink, ComponentList, BCard, BCardTitle, BAlert, BRow, BCol, BBadge, BButton,
        VueAutosuggest, BFormRadio, Substance
    },
    props: {
        compactView: {
            type: Boolean,
            default: false
        },
        product: {
            type: Object,
            default() {
                return {
                    hasConcernSubstances: null
                };
            }
        },
        id: {
            required: true
        },
        showComponentsSubstances: {
            type: Boolean,
            default: false,
        },
        concernSubstances: {
            type: Array,
            default() {
                return [];
            }
        },
        excludedSubstances: {
            type: Array,
            default() {
                return [];
            }
        },
        legislations: {
            type: Array,
            default() {
                return [];
            }
        }
    },
    data() {
        return {
            suggestions: [],
            searchTimer: null,
            inputProps: {
                class: 'form-control w-100',
            },

            substances: [],
            substanceConfiguration: {},
            //hasConcernElements: this.concernSubstances.length > 0,

            components: [],
            complexProductCompositionPromise: null,
            isComponentsLoaded: false,

            substanceLists: []
        };
    },
    methods: {
        productHasConcernElements(hasConcernElements) {
            // this.hasConcernElements = hasConcernElements;
            this.$emit('hasConcernSubstances', hasConcernElements);
        },

        substanceConfigurationChanged(substance, properties) {
            this.$emit('substancePropertiesChanged', substance, properties);
        },

        include({item}) {
            if (!this.isSelected({item: item})) {
                this.$emit('include', item, this.substanceLists.map(list => list.id));
            }
        },

        exclude(substance) {
            this.$emit('exclude', substance.substance);
        },

        isSelected({item}) {
            return this.substances.find((s) => s.id === item.id || (s.substance && s.substance.id === item.id));
        },

        findSubstance(qs) {
            if (!qs) {
                return;
            }

            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                SubstanceApi.search(qs).then(r => {
                    this.suggestions = [{
                        data: r.data.map(item => {
                            item.configuration = {};
                            item.details = [];
                            return item;
                        }),
                    }];
                });
            }, 600);
        },

        getSuggestionValue() {
            return '';
        },

        loadProductComponentsConcernElements() {
            if (!this.showComponentsSubstances) {
                return;
            }
            if (this.product.id && this.complexProductCompositionPromise === null) {
                this.complexProductCompositionPromise = ProductApi
                    .complexProductComposition(this.id)
                    .then(r => {
                        this.components = r.data;
                    });
            }
        },
        calculateProductDoesNotHaveConcernSubstances() {
            if (!this.isHasConcernSubstancesSwitcherVisible) {
                return false;
            }
            if (null === this.hasConcernElements) {
                if (this.concernSubstances.length || this.excludedSubstances.length) {

                    if (this.substanceLists.length === 0) {
                        return;
                    }

                    const substances = this.concernSubstances.concat(this.excludedSubstances);
                    Promise
                        .all(this.substanceLists.map(l => l.getSubstanceMap()))
                        .then(maps => {
                            const listSubstances = maps.reduce((value, listMap) => {
                                return {...value, ...listMap};
                            }, {});

                            if (Object.keys(listSubstances).length === substances.length) {
                                this.productHasConcernElements(false);
                            }
                        });


                    // getAllSubstanceLists()
                    //     .then(lists => {
                    //         const substances = this.concernSubstances.concat(this.excludedSubstances);
                    //         Promise
                    //             .all(lists.map(l => l.getSubstanceMap()))
                    //             .then(maps => {
                    //                 const listSubstances = maps.reduce((value, listMap) => {
                    //                     return {...value, ...listMap};
                    //                 }, {});
                    //
                    //                 if (Object.keys(listSubstances).length === substances.length) {
                    //                     this.productHasConcernElements(false);
                    //                 }
                    //             });
                    //     });
                }
            }
        },
        retrieveSubstanceLists() {
            getAllSubstanceLists().then(lists => {
                if (this.legislations.length === 0) {
                    this.substanceLists = lists;
                    return;
                }

                this.loadSubstanceListVisibilityCategories().then(() => {
                    const productContext = this.buildSubstanceListVisibilityProductContext();
                    const graph = this.createSubstanceListReferenceGraph(lists);

                    this.substanceLists = graph.resolveActiveLists(this.legislations, productContext);
                });
            });
        }
    },

    created() {
        this.retrieveSubstanceLists();
        // this.isComponentsLoaded = false;
        setTimeout(() => {
            this.loadProductComponentsConcernElements();
        }, 400);
    },

    computed: {
        hasConcernElements() {
            return this.product.hasConcernSubstances;
        },
        isHasConcernSubstancesSwitcherVisible() {
            // if show components substances mode is activated we need to hide switcher
            // #HAM-150
            if (this.showComponentsSubstances) {
                return false;
            }

            // if there is any concern substances hide switcher
            if (this.concernSubstances.length > 0) {
                return false;
            }

            return this.product.hasConcernSubstances !== true;
        }
    },

    watch: {
        excludedSubstances() {
            this.calculateProductDoesNotHaveConcernSubstances();
        },
        concernSubstances(value) {
            this.substances = value;
            this.calculateProductDoesNotHaveConcernSubstances();
        },
        showComponentsSubstances(newV, oldV) {
            this.loadProductComponentsConcernElements();
        },
        product() {
            this.loadProductComponentsConcernElements();
        },
        legislations() {
            this.retrieveSubstanceLists();
        }
    }
}
</script>
